import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Sparkles, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";

export default function AuthModal() {
  const { authOpen, setAuthOpen, login, register, demoLogin } = useAuth();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");

  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPass, setRegPass] = useState("");

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const doForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", {
        email: forgotEmail.trim(),
        base_url: window.location.origin,
      });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setAuthOpen(false);
    setError("");
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(loginEmail, loginPass);
      toast.success(`Welcome back, ${u.name}`);
      close();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setError("");
    const name = regName.trim();
    const email = regEmail.trim();
    if (!name) {
      setError("Please enter a display name.");
      return;
    }
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    if (regPass.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }
    setLoading(true);
    try {
      const u = await register({ name, email, password: regPass, is_non_supervisory: true });
      toast.success(`Account created — welcome, ${u.name}`);
      close();
    } catch (err) {
      setError(
        formatApiError(err.response?.data?.detail) ||
          err.message ||
          "Could not create your account. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const doDemo = async () => {
    setLoading(true);
    try {
      const u = await demoLogin();
      toast.success(`Exploring as ${u.name}`);
      close();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0" data-testid="auth-modal">
        <DialogTitle className="sr-only">Sign in or register</DialogTitle>
        <DialogDescription className="sr-only">Access your private LeadBeacon account to write anonymous reviews.</DialogDescription>
        <div className="bg-slate-900 px-6 py-5 lb-grain">
          <div className="flex items-center gap-2 text-white">
            <ShieldCheck size={22} className="text-emerald-400" />
            <span className="font-display text-2xl" style={{ fontWeight: 800 }}>
              my360<span className="text-emerald-400">boss</span>
            </span>
          </div>
          <p className="text-slate-400 text-sm mt-1">
            Your identity stays private. Reviews are always anonymous.
          </p>
        </div>

        <div className="p-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full mb-5">
              <TabsTrigger value="login" data-testid="auth-tab-login">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="auth-tab-register">Register</TabsTrigger>
            </TabsList>

            {error && (
              <div data-testid="auth-error" className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input data-testid="login-email-input" type="email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="Work or personal email" />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input data-testid="login-password-input" type="password" required value={loginPass} onChange={(e) => setLoginPass(e.target.value)} placeholder="••••••••" />
                </div>
                <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-emerald-600 rounded-full">
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => { setError(""); setForgotSent(false); setForgotEmail(loginEmail); setTab("forgot"); }}
                  className="w-full text-center text-sm text-slate-500 hover:text-emerald-700"
                >
                  Forgot your password?
                </button>
              </form>
            </TabsContent>

            <TabsContent value="forgot">
              <button type="button" onClick={() => { setError(""); setTab("login"); }} className="flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-700 mb-4">
                <ArrowLeft size={15} /> Back to sign in
              </button>
              {forgotSent ? (
                <div data-testid="forgot-sent" className="text-center py-4">
                  <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                    <ShieldCheck className="text-emerald-600" />
                  </div>
                  <p className="font-semibold text-slate-800">Check your email</p>
                  <p className="text-sm text-slate-500 mt-1">
                    If an account exists for <b>{forgotEmail}</b>, we've sent a link to reset your password. It expires in 30 minutes.
                  </p>
                </div>
              ) : (
                <form onSubmit={doForgot} className="space-y-4">
                  <p className="text-sm text-slate-500">Enter your account email and we'll send you a secure reset link.</p>
                  <div>
                    <Label>Email</Label>
                    <Input data-testid="forgot-email-input" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@example.com" />
                  </div>
                  <Button data-testid="forgot-submit-btn" type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-emerald-600 rounded-full">
                    {loading ? "Sending..." : "Send reset link"}
                  </Button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-4">
                <div>
                  <Label>Display name</Label>
                  <Input data-testid="register-name-input" required value={regName} onChange={(e) => setRegName(e.target.value)} placeholder="Kept private" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input data-testid="register-email-input" type="email" required value={regEmail} onChange={(e) => setRegEmail(e.target.value)} placeholder="Work or personal email" />
                  <p className="text-xs text-slate-400 mt-1">Use a work <span className="text-slate-500">or</span> personal email — whatever you prefer. It's never shown to anyone.</p>
                </div>
                <div>
                  <Label>Password</Label>
                  <Input data-testid="register-password-input" type="password" required value={regPass} onChange={(e) => setRegPass(e.target.value)} placeholder="At least 6 characters" />
                  <PasswordStrengthMeter password={regPass} testId="register-password-strength" />
                  <p className={`text-xs mt-1 ${regPass && regPass.length < 6 ? "text-rose-500" : "text-slate-400"}`}>
                    Must be at least 6 characters.
                  </p>
                </div>
                <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-emerald-600 rounded-full">
                  {loading ? "Creating..." : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="relative my-5">
            <div className="border-t border-slate-200" />
            <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-white px-3 text-xs font-mono uppercase tracking-widest text-slate-400">
              or
            </span>
          </div>

          <Button data-testid="demo-login-btn" onClick={doDemo} disabled={loading} variant="outline" className="w-full gap-2 rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <Sparkles size={16} /> Explore as Employee (Demo)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
